import { useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { asUserId, type UserId } from '@leanin/shared/ids';
import { useAuthedSession } from '@/lib/use-session';
import {
  mapProfileSelfToView,
  memberSinceFromCreatedAt,
} from '@/components/profile/mapper';
import {
  ProfileChrome,
  type ProfileHeaderData,
} from '@/components/profile/ProfileChrome';
import { ProfileChromeSkeleton } from '@/components/profile/ProfileChromeSkeleton';
import { ProfileContentView } from '@/components/profile/ProfileContentView';
import { ErrorState, useDelayedLoading } from '@leanin/ui';
import { RetryButton } from '@/components/states/index';
import { identityKeys } from '@/lib/queries/identity';
import { groupKeys } from '@/lib/queries/groups';
import { feedKeys } from '@/lib/queries/feed';
import { authoredPostToFeedPost } from '@/components/feed/feed-item-to-post';
import { useFormatDate } from '@/lib/format-date.client';
import type { Profile } from '@/components/profile/types';

export const Route = createFileRoute('/_authed/profile/')({
  component: ProfilePage,
});

function buildHeadline(data: {
  jobTitle?: string | null;
  company?: string | null;
  bio?: string | null;
}): string {
  const role = data.jobTitle ?? '';
  const company = data.company ?? '';
  if (role && company) return `${role} at ${company}`;
  return role || company || data.bio?.slice(0, 80) || '';
}

function ProfilePage() {
  // Use the cached session member ID so all queries fire in parallel rather
  // than waiting for meQuery to resolve before enabling the collateral fetches.
  const session = useAuthedSession();
  const userId: UserId | null = session ? asUserId(session.userId) : null;
  const fmt = useFormatDate();
  const t = useTranslations('Profile');

  const meQuery = useQuery(identityKeys.me());

  const me = meQuery.data;

  // The Circles aside is a COMPLETE list of the viewer's own circles, not a
  // browse teaser — there is no "see all" target for it (`/circles` is the
  // org-wide public browse page, not the viewer's membership list). A member
  // has no domain cap on how many circles they join, so a small `pageSize`
  // silently drops the rest. Mirror the sibling complete-list window used for
  // this same `/members/me/circles` endpoint (`_public/circles/index.tsx`
  // `MINE_PAGE_SIZE = 100`): fetch a window large enough that no member's own
  // circles are truncated. Networks on this same card already fetch complete —
  // `/me/networks` has no pagination (server default window, response is
  // `{ items }` only) — so no change is needed there.
  const circlesQuery = useQuery({
    ...groupKeys.circle.mine({ pageSize: 100 }),
    enabled: userId !== null,
  });
  const networksQuery = useQuery({
    ...groupKeys.network.mine(),
    enabled: userId !== null,
  });
  // Profile post list pagination. Server `limit` caps at 50 (see
  // posts/routes/list-posts-by-author.ts). Start with 5; the "Load
  // more" button adds 5 each click.
  const POSTS_PAGE = 5;
  const POSTS_MAX = 50;
  const [postsLimit, setPostsLimit] = useState<number>(POSTS_PAGE);
  // `authorPosts` needs a concrete id; the `enabled` gate keeps the query
  // dormant until the session resolves, so the placeholder id is never
  // fetched with.
  const postsQuery = useQuery({
    ...feedKeys.authorPosts(userId ?? '', postsLimit),
    enabled: userId !== null,
    // Bumping `postsLimit` rotates the query key. Without
    // `keepPreviousData` React Query treats the new key as a fresh
    // query with no data, which flips `postsQuery.isLoading` true and
    // collapses the whole page to its skeleton until the new page
    // lands — the visible "spazz" reported. With the placeholder, the
    // previous page of posts stays painted while the next page fetches.
    placeholderData: keepPreviousData,
  });

  const showSkeleton = useDelayedLoading(meQuery.isLoading || !me);
  if (showSkeleton) return <ProfileChromeSkeleton />;
  if (meQuery.isLoading || !me) return null;
  if (meQuery.isError) {
    return (
      <ErrorState
        title={t('errors.loadTitle')}
        body={t('errors.loadBody')}
        primary={<RetryButton />}
      />
    );
  }

  // `/profile` is the SELF-VIEW preview: it renders the public-facing
  // profile body as another member would see it, so the leader can verify
  // how their profile reads before editing. The chrome keeps `isViewer:
  // true` so the "Edit profile" CTA appears on the header; the body itself
  // is always read-only — editing happens at /settings/account.
  const header: ProfileHeaderData = {
    id: me.id,
    name:
      [me.firstName, me.lastName].filter(Boolean).join(' ').trim() ||
      t('memberFallback'),
    headline: buildHeadline(me),
    location: me.location ?? '',
    memberSince: memberSinceFromCreatedAt(me.createdAt, fmt.monthYear),
    photo: me.photoUrl ?? undefined,
    photoLarge: me.photoLargeUrl ?? undefined,
    coverPhotoUrl: me.coverPhotoUrl ?? undefined,
    isViewer: true,
    isFollowing: false,
    isNetworkLeader: (networksQuery.data?.items ?? []).some(
      (n) => n.role === 'leader',
    ),
  };

  // Built from the resolved `me` identity immediately — the collateral
  // queries (circles, networks, posts) fill in independently and paint their
  // own region skeletons, so none of them gates the primary profile body.
  const circles = circlesQuery.data?.items ?? [];
  const networks = networksQuery.data?.items ?? [];
  const posts = postsQuery.data ?? [];

  const profile: Profile = mapProfileSelfToView(me, {
    circles: circles.map((c) => ({
      id: c.id,
      name: c.name,
      role: c.role === 'leader' ? 'Leader' : 'Member',
      meta: t('circle.memberCount', { count: c.memberCount }),
      imageUrl: c.imageUrl,
    })),
    networks: networks.map((n) => ({
      id: n.id,
      name: n.name,
      role: n.role === 'leader' ? ('Leader' as const) : ('Member' as const),
      memberCount: n.memberCount,
      imageUrl: n.imageUrl,
    })),
    posts: posts.map((p) => authoredPostToFeedPost(p, fmt.relativeShort)),
  }, fmt.monthYear);

  const postsFetched = postsQuery.data?.length ?? 0;
  const hasMorePosts = postsFetched >= postsLimit && postsLimit < POSTS_MAX;

  return (
    <ProfileChrome header={header}>
      <ProfileContentView
        profile={profile}
        postsLoading={postsQuery.isLoading}
        asideLoading={circlesQuery.isLoading || networksQuery.isLoading}
        hasMorePosts={hasMorePosts}
        loadingMorePosts={postsQuery.isFetching}
        onLoadMorePosts={() =>
          setPostsLimit((n) => Math.min(n + POSTS_PAGE, POSTS_MAX))
        }
      />
    </ProfileChrome>
  );
}
